import { AxiosResponse } from "axios";
import { apiClient } from "@store/services/checkoutClient";
import { FARE_DEFAULT } from "@constants/fares";
import { BOOKING_ROUTE, PAXCONNECT } from "@constants/api";
import CheckoutSummary from "@store/checkout/interfaces/checkoutSummary";
import Inquiry from "@store/checkout/interfaces/inquiry";
import SiteSettings from "@store/checkout/interfaces/siteSettings";
import { securePost, secureGet } from "@store/services/axiosRequestWrapper";

export async function fetchResponse(apiEndpoint: string, type: string, tripId: string | number, appointmentHash: string, apiToken: string, sessionId: string, fare: string = FARE_DEFAULT, locale: string): Promise<AxiosResponse> {
    const url = apiEndpoint + BOOKING_ROUTE + type +
    '?tripId=' + tripId +
    '&appointmentId=' + appointmentHash +
    '&fare=' + fare +
    '&sessionId=' + sessionId

    return await secureGet(url, apiToken, locale);
}

export async function fetchAppointment(apiEndpoint: string, type: string, tripId: string | number, appointmentHash: string, apiToken: string, sessionId: string, flightId: string, fare: string = FARE_DEFAULT, locale: string): Promise<AxiosResponse> {
    const url = apiEndpoint + BOOKING_ROUTE + type +
      '?tripId=' + tripId +
      '&appointmentId=' + appointmentHash +
      '&fare=' + fare +
      '&sessionId=' + sessionId +
      '&flightId=' + flightId;

    return await secureGet(url, apiToken, locale);
}

//Initially, the endpoint must be called without the fares query parameter. Only when a fare has been selected, it should be appended as a parameter.
export async function fetchBookingSteps(apiEndpoint: string, type: string, tripId: string | number, appointmentHash: string, apiToken: string, sessionId: string, fare: string = '', locale: string): Promise<AxiosResponse> {
    let requestUrl = apiEndpoint + BOOKING_ROUTE + type +
        '?tripId=' + tripId +
        '&appointmentId=' + appointmentHash +
        '&sessionId=' + sessionId

    if (fare !== '') {
        requestUrl += '&fare=' + fare;
    }

    return await secureGet(requestUrl, apiToken, locale);
}

export async function postResponse(apiEndpoint: string, type: string, apiToken: string | number, data: any, locale: string): Promise<AxiosResponse> {
    return await securePost(apiEndpoint + BOOKING_ROUTE + type, apiToken, locale, data);
}

export async function postDeal(apiEndpoint: string, type: string, apiToken: string, inquiry: Inquiry, locale: string): Promise<AxiosResponse> {
    return await securePost(apiEndpoint + BOOKING_ROUTE + type, apiToken, locale, inquiry);
}

export async function fetchDateAndPriceData(apiEndpoint: string, uid: number, filter: string, apiToken: string, locale: string): Promise<AxiosResponse> {
    const url = apiEndpoint + BOOKING_ROUTE +
      'GetTripPrices?tripId=' + uid +
      '&airportCode=' + filter;

    return await secureGet(url, apiToken, locale);
}

export async function fetchFlights(apiEndpoint: string, tripId: string | number, appointmentHash: string, apiToken: string, sessionId: string, departureAirport: string | null, fare: string = FARE_DEFAULT, locale: string): Promise<AxiosResponse> {
    const url = apiEndpoint + BOOKING_ROUTE +
        'GetFlights?tripId=' + tripId +
        '&appointmentAirportId=' + appointmentHash +
        '&departureAirport=' + departureAirport +
        '&fare=' + fare +
        '&sessionId=' + sessionId;

    return await secureGet(url, apiToken, locale);
}

export async function fetchPaymentMethods(siteSettings: SiteSettings): Promise<AxiosResponse> {
    const base = siteSettings.basePath.replaceAll('/', '');
    return apiClient.get( siteSettings.baseUrl + base + '/api/payment/options');
}

export async function fetchFares(apiEndpoint: string, tripId: string | number, appointmentHash: string, apiToken: string, sessionId: string, departureAirport: string | null, locale: string): Promise<AxiosResponse> {
    const url = apiEndpoint + BOOKING_ROUTE +
        'GetFares?tripId=' + tripId +
        '&appointmentAirportId=' + appointmentHash +
        '&departureAirport=' + departureAirport +
        '&sessionId=' + sessionId;

    return await secureGet(url, apiToken, locale);
}

export async function updateAppointment(apiEndpoint: string, type: string, tripId: string | number, appointmentHash: string, apiToken: string, fare: string = FARE_DEFAULT, locale: string): Promise<AxiosResponse> {
    const url = apiEndpoint + BOOKING_ROUTE + type +
        '?tripId=' + tripId +
        '&appointmentId=' + appointmentHash +
        '&fare=' + fare +
        '&useNew=true' // added temporary query param useNew (false = old implementation, true = new implementation)

    return await secureGet(url, apiToken, locale);
}

export async function saveRequest(apiEndpoint: string, data: any, bookingStep: string, apiToken: string, requestId: string = '', locale: string): Promise<AxiosResponse> {
    let url: string = apiEndpoint + BOOKING_ROUTE + 'SaveRequest?bookingStep=' + bookingStep;

    if (requestId !== '') {
        url = url + '&id=' + requestId
    }

    return await securePost(url, apiToken, locale, data);
}

export async function logRequest(apiEndpoint: string, type: string, data: any): Promise<AxiosResponse> {
    return apiClient.post(
        apiEndpoint + type,
        data
    );
}

export async function logError(apiEndpoint: string, type: string, error: any): Promise<AxiosResponse> {
    return apiClient.post(
        apiEndpoint + type,
        error
    );
}

export async function restoreRequest(apiEndpoint: string, requestId: string, apiToken: string, locale: string): Promise<AxiosResponse> {
    const url = apiEndpoint + BOOKING_ROUTE + 'RestoreRequest?id=' + requestId + '&useNewImplementation=true';

    return await secureGet(url, apiToken, locale);
}

export async function getPaxConnectImportUri(apiEndpoint: string, apiToken: string, checkoutSummary: CheckoutSummary, bookingStep: string, locale: string): Promise<AxiosResponse> {
    return await securePost(apiEndpoint + PAXCONNECT + 'GetPaxConnectImportUri?bookingStep=' + bookingStep, apiToken, locale, checkoutSummary);
}

export async function fetchCountries(baseUrl: string, locale: string): Promise<AxiosResponse> {
    return apiClient.get(baseUrl + 'fileadmin/configuration/countries/country_v3_' + locale + '.json')
}
